import { ENABLE_LOCALIZED_BRAND_PAGE_TESTIMONIALS_241015 } from "@faire/web-api/indigofair/settings/ENABLE_LOCALIZED_BRAND_PAGE_TESTIMONIALS_241015";
import { THREE_LETTER_PRIMARY_COUNTRY_CODES } from "@faire/web/common/consts/THREE_LETTER_PRIMARY_COUNTRY_CODES";
import {
  DEPRECATED_backwardsCompatibleValues,
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web/common/localization/localize";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { useParams } from "next/navigation";
import React, { useMemo } from "react";

import { Testimonials } from "@faire/retailer/app/visitor/[locale]/(pagesWithModal)/(defaultHeader)/brand/[brandToken]/(rsc)/rsc/_sections/Testimonials/Testimonials";
import { ITestimonial } from "@faire/retailer/components/TestimonialCarousel/TestimonialCarousel";
import { getIsRobot } from "@faire/retailer/serialized-data/getIsRobot";
import { getSessionCountry } from "@faire/retailer/serialized-data/getSessionCountry";
import { useEffectAssignWhen } from "@faire/retailer/settings/useEffectAssignWhen";
import { useSettingLocalizedBrandTestimonials } from "@faire/retailer/settings/useSettingLocalizedBrandTestimonials";

const completistTestimonial = (
  strictLocalize: StrictLocalizeFunction
): ITestimonial => {
  return {
    avatarImageUrl:
      "https://cdn.faire.com/fastly/f6385daef95d4fbf17bd60dcbcc9244008ae4dc7c91caa5ee5c338435c47548c.png",
    content: (
      <StrictLocalMsg
        defaultMessage="“We're stocked in some of the best stores and art instituions in the world thanks to Faire, and use it to source products for our two shops in South East London too. Some of our strongest brand and supplier relationships have come through Faire.”"
        description={{
          text: "Testimonial from retailer about Faire",
          img: "https://cdn.faire.com/fastly/e06b6390b16f35752d0201b417d9aabcf8f0b198ae94ec121f0d1420409badcb.png",
        }}
      />
    ),
    reviewerInfo: strictLocalize({
      defaultMessage:
        "Jana Starcevic, co-founder of The Completist in London, UK",
      description: {
        text: "Name of retailer, their shop, and the shop location",
        img: "https://cdn.faire.com/fastly/e06b6390b16f35752d0201b417d9aabcf8f0b198ae94ec121f0d1420409badcb.png",
      },
    }),
  };
};

const earlOfEastTestimonial = (
  strictLocalize: StrictLocalizeFunction
): ITestimonial => {
  return {
    avatarImageUrl:
      "https://cdn.faire.com/fastly/808988fcf046cd1f0b85a2fff746b254c49eb72e32a4688b6ca322666319f2d3.jpeg",
    content: (
      <StrictLocalMsg
        defaultMessage="“Faire allowed us to discover and source unique products for our own stores, all while simplifying the buying and selling process.”"
        description={{
          text: "Testimonial from retailer about Faire",
          img: "https://cdn.faire.com/fastly/e06b6390b16f35752d0201b417d9aabcf8f0b198ae94ec121f0d1420409badcb.png",
        }}
      />
    ),
    reviewerInfo: strictLocalize({
      defaultMessage: "Niko Dafkos, founder of Earl of East in London, UK",
      description: {
        text: "Name of retailer, their shop, and the shop location",
        img: "https://cdn.faire.com/fastly/e06b6390b16f35752d0201b417d9aabcf8f0b198ae94ec121f0d1420409badcb.png",
      },
    }),
  };
};

const ukTestimonials = (
  strictLocalize: StrictLocalizeFunction
): ITestimonial[] => [
  completistTestimonial(strictLocalize),
  {
    avatarImageUrl:
      "https://cdn.faire.com/fastly/5a7a894ea15a8c07901e7f5128204f45b9945380cc9aa6b3760c01a892673ec2.jpeg",
    content: (
      <StrictLocalMsg
        defaultMessage="“Faire is an amazing platform that opened the door to us selling our jewellery to retailers and allowed us to easily grow that side of our business. Then, when we opened our first retail space, it was a fantastic way for us to find unique stock.”"
        description={{
          text: "Testimonial from retailer about Faire",
          img: "https://cdn.faire.com/fastly/e06b6390b16f35752d0201b417d9aabcf8f0b198ae94ec121f0d1420409badcb.png",
        }}
      />
    ),
    reviewerInfo: strictLocalize({
      defaultMessage: "Isobel Keeys, founder of And Danu in Whitstable, UK",
      description: {
        text: "Name of retailer, their shop, and the shop location",
        img: "https://cdn.faire.com/fastly/e06b6390b16f35752d0201b417d9aabcf8f0b198ae94ec121f0d1420409badcb.png",
      },
    }),
  },
  earlOfEastTestimonial(strictLocalize),
];

const frTestimonials = (
  strictLocalize: StrictLocalizeFunction
): ITestimonial[] => [
  {
    avatarImageUrl:
      "https://cdn.faire.com/fastly/b1b5a4d4cac0318ec124907ceb6f740d2bbf5c663911b0d78363e4fe2b9231f2.jpeg",
    content: (
      <StrictLocalMsg
        defaultMessage="“Faire has allowed us to discover amazing brands in France, Europe, and globally. We've built wonderful relationships and can now offer our customers the latest products created by passionate creators.”"
        description={{
          text: "Testimonial from retailer about Faire",
          img: "https://cdn.faire.com/fastly/5888ffb3ba6e1a574029b97f053501d2af3e41ee8bc46a0df1d25c3d0b431ac7.png",
        }}
      />
    ),
    reviewerInfo: strictLocalize({
      defaultMessage:
        "Marie Dardaine, founder of Bakers & Brothers in Paris, France",
      description: {
        text: "Name of retailer, their shop, and the shop location",
        img: "https://cdn.faire.com/fastly/5888ffb3ba6e1a574029b97f053501d2af3e41ee8bc46a0df1d25c3d0b431ac7.png",
      },
    }),
  },
  completistTestimonial(strictLocalize),
  earlOfEastTestimonial(strictLocalize),
];

const testimonials = (
  isUSA: boolean,
  isMobile: boolean,
  strictLocalize: StrictLocalizeFunction
): ITestimonial[] => [
  {
    avatarImageUrl:
      "https://cdn.faire.com/fastly/72f810f95c17f7e377a96d5fa37775a7552c47cfd0cf40074a639b4d80803e05.png",
    content: (
      <StrictLocalMsg
        // eslint-disable-next-line @faire/no-mid-sentence-linebreaks
        defaultMessage="“If you aren’t using Faire, you’re missing a great{lineBreak}opportunity. Start today and deal with a very professional{lineBreak}company helping all shopkeepers buy quality products{lineBreak}from independent makers and artisans.”"
        values={DEPRECATED_backwardsCompatibleValues({
          lineBreak: isMobile ? " " : <br />,
        })}
      />
    ),
    reviewerInfo: isUSA
      ? strictLocalize({
          defaultMessage:
            "Cindy Davenport, Cinport Floral Studio & Gift Designs by Cinport",
        })
      : strictLocalize({
          defaultMessage:
            "Cindy Davenport, owner of Floral Studio & Gift Designs by Cinport in Florida, USA",
        }),
  },
  {
    avatarImageUrl:
      "https://cdn.faire.com/fastly/9cd7f99ab4862a8332e44f3876063e11cdf0a37efa2f634b3bc5b7c8a0fc3ca0.png",
    content: (
      <StrictLocalMsg
        // eslint-disable-next-line @faire/no-mid-sentence-linebreaks
        defaultMessage="“As our business grows, Faire has allowed us to easily try products{lineBreak}that we might not have taken the chance on before. The fact that{lineBreak}they’ve tailored the selections to fit our brand has simplified{lineBreak}our buying processes significantly.”"
        values={DEPRECATED_backwardsCompatibleValues({
          lineBreak: isMobile ? " " : <br />,
        })}
      />
    ),
    mobileContent: (
      <StrictLocalMsg defaultMessage="“As our business grows, Faire has allowed us to easily try products that we might not have taken the chance on before.”" />
    ),
    reviewerInfo: isUSA
      ? strictLocalize({
          defaultMessage: "Jake France + Richard Rolfe, Boys Fort",
        })
      : strictLocalize({
          defaultMessage:
            "Jake France + Richard Rolfe, owners of Boys Fort in Portland, USA",
        }),
  },
  {
    avatarImageUrl:
      "https://cdn.faire.com/fastly/0ded13b33fd5d5c505deb0bd98ec2fb3bc283d2fb09d10ebd82995c4bd326df9.jpeg",
    content: (
      <StrictLocalMsg
        // eslint-disable-next-line @faire/no-mid-sentence-linebreaks
        defaultMessage="“I love Faire because I don’t have to go to trade shows.{lineBreak}Faire’s website is 100% more user-friendly and better{lineBreak}designed than other wholesale sites.”"
        values={DEPRECATED_backwardsCompatibleValues({
          lineBreak: isMobile ? " " : <br />,
        })}
      />
    ),
    reviewerInfo: isUSA
      ? strictLocalize({
          defaultMessage: "John Frechette, MADE",
        })
      : strictLocalize({
          defaultMessage: "John Frechette, owner of MADE in Jackson, USA",
        }),
  },
];

export const TestimonialModule = React.forwardRef<HTMLDivElement>(
  (_props, ref) => {
    const { strictLocalize } = useStrictLocalization();
    const sessionCountry = getSessionCountry();
    const isUSA = sessionCountry === THREE_LETTER_PRIMARY_COUNTRY_CODES.USA;

    const isLocalizedBrandTestimonials = useSettingLocalizedBrandTestimonials();
    const isRobot = getIsRobot();
    const { locale } = useParams<{ locale: string }>() ?? { locale: "en-us" };

    const countryCode = getSessionCountry();

    useEffectAssignWhen(
      ENABLE_LOCALIZED_BRAND_PAGE_TESTIMONIALS_241015,
      countryCode === "FRA" || countryCode === "GBR"
    );

    const localizedTestimonials = useMemo(() => {
      if (
        isLocalizedBrandTestimonials &&
        ((isRobot && locale === "en-gb") || (!isRobot && countryCode === "GBR"))
      ) {
        return ukTestimonials(strictLocalize);
      }

      if (
        isLocalizedBrandTestimonials &&
        ((isRobot && locale === "fr") || (!isRobot && countryCode === "FRA"))
      ) {
        return frTestimonials(strictLocalize);
      }

      return undefined;
    }, [
      isLocalizedBrandTestimonials,
      isRobot,
      locale,
      countryCode,
      strictLocalize,
    ]);

    const testimonialsMobile = testimonials(isUSA, true, strictLocalize);
    const testimonialsDesktop = testimonials(isUSA, false, strictLocalize);

    const brandCountHeader = (
      <StrictLocalMsg
        defaultMessage="TRUSTED BY HUNDREDS OF THOUSANDS OF RETAILERS"
        description={{
          text: "testimonials header",
          img: "https://cdn.faire.com/fastly/9a1d255f3d61578599239ed48f24f1b0b3476ee6b2cf1c70b14c5e365081c63d.png",
        }}
      />
    );

    return (
      // already hardcoded with `hideStoryLink` and `withRetailerCount` true
      <Testimonials
        itemsMobile={localizedTestimonials ?? testimonialsMobile}
        itemsDesktop={localizedTestimonials ?? testimonialsDesktop}
        brandCountHeader={brandCountHeader}
        ref={ref}
      />
    );
  }
);
